import { useNavigate } from "@tanstack/react-router";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { z } from "zod";
import { InputText, Modal, Select, type SelectOption } from "@/deps/ui-library";
import { useBankAccounts, useEmployees, useRecordEmployeeMovement } from "@/api/queries";
import { today } from "@/lib/dates";
import { DateField } from "@/components/DateField";

const schema = z.object({
  partyId: z.string().min(1, "Choose the employee."),
  controlRole: z.enum(["employee_payable", "employee_advance"]),
  amount: z.string().refine((value) => Number(value) > 0, "Enter an amount above zero."),
  date: z.string().min(1, "Choose the date."),
  cashAccountId: z.string().min(1, "Choose the account."),
  reference: z.string(),
  note: z.string(),
});
type Values = z.infer<typeof schema>;

const resolver: Resolver<Values> = (values) => {
  const parsed = schema.safeParse(values);
  if (parsed.success) return { values: parsed.data, errors: {} };

  const errors: Record<string, { type: string; message: string }> = {};
  for (const issue of parsed.error.issues) {
    errors[String(issue.path[0])] ??= { type: issue.code, message: issue.message };
  }
  return { values: {}, errors };
};

const first = (value: SelectOption | SelectOption[] | null) =>
  String((Array.isArray(value) ? value[0] : value)?.value ?? "");

const ACCOUNTS = [
  { value: "employee_payable", label: "Employee payable" },
  { value: "employee_advance", label: "Advances to employees" },
];

/**
 * Money out to an employee, or back from one (ADR-0060). It posts at once, unless the payment rule wants
 * it approved first, when it opens as a draft to submit.
 */
export function EmployeeMovementDialog({
  kind,
  partyId = "",
  onClose,
}: {
  kind: "payment" | "receipt";
  partyId?: string;
  onClose: () => void;
}) {
  const paying = kind === "payment";
  const { data: employees } = useEmployees();
  const { data: banks } = useBankAccounts();
  const record = useRecordEmployeeMovement();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<Values>({
    resolver,
    defaultValues: {
      partyId,
      controlRole: paying ? "employee_payable" : "employee_advance",
      amount: "",
      date: today(),
      cashAccountId: "",
      reference: "",
      note: "",
    },
  });

  return (
    <Modal.Form
      open
      title={paying ? "Pay employee" : "Receive from employee"}
      subtitle={
        paying
          ? "Debits the employee's ledger and credits the account it's paid from. A debit balance is recoverable from them."
          : "Credits the employee's ledger and debits the account it's received into."
      }
      submitLabel={paying ? "Record payment" : "Record receipt"}
      busy={record.isPending}
      error={record.error ? (record.error as Error).message : null}
      onClose={onClose}
      onSubmit={() =>
        void handleSubmit((values) =>
          record.mutate(
            { ...values, kind },
            {
              onSuccess: ({ id, number }) => {
                onClose();
                if (number === null) {
                  void navigate({ to: "/accounting/employees/movements/$id", params: { id } });
                }
              },
            },
          ),
        )()
      }
    >
      <div className="flex flex-col gap-3">
        <Controller
          control={control}
          name="partyId"
          render={({ field }) => (
            <Select
              required
              label="Employee"
              options={(employees ?? [])
                .filter((employee) => employee.isActive)
                .map((employee) => ({ value: employee.id, label: employee.name }))}
              value={field.value}
              errorText={errors.partyId?.message}
              onChange={(details) => field.onChange(first(details.value))}
            />
          )}
        />
        <Controller
          control={control}
          name="controlRole"
          render={({ field }) => (
            <Select
              required
              label="Account"
              options={ACCOUNTS}
              value={field.value}
              showClearButton={false}
              onChange={(details) => field.onChange(first(details.value))}
            />
          )}
        />
        <InputText
          required
          label="Amount"
          inputMode="decimal"
          errorText={errors.amount?.message}
          {...register("amount")}
        />
        <Controller
          control={control}
          name="date"
          render={({ field }) => (
            <DateField
              required
              label="Date"
              error={errors.date?.message}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={control}
          name="cashAccountId"
          render={({ field }) => (
            <Select
              required
              label={paying ? "Paid from" : "Received into"}
              options={(banks ?? []).map((bank) => ({ value: bank.accountId, label: bank.name }))}
              value={field.value}
              errorText={errors.cashAccountId?.message}
              onChange={(details) => field.onChange(first(details.value))}
            />
          )}
        />
        <InputText label="Reference" {...register("reference")} />
        <InputText label="Note" placeholder="What this is for" {...register("note")} />
      </div>
    </Modal.Form>
  );
}
